import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {
  Typography,
  IconButton,
  MenuItem,
  ListItemText
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '../CommonComponents/Menu';
import Snackbar from '../CommonComponents/Snackbar';
import {
  DialogRequesterDetails,
  DialogAssigneeDetails,
  DialogNoLead,
  DialogAssign,
  DialogDelete,
} from '../CommonComponents/DialogBox';
import SummaryDrawer from '../Dashboard/Common/SummaryDrawer';
import ManageTeamDrawer from '../Dashboard/Common/ManageTeamDrawer';
import {RequestStatusId, UserRole} from '../../ApplicationConfig/constants';
import {UserInfoContext} from '../../StateObjects/UserInfoContext';

const PREFIX = 'RequestContextMenu';

const classes = {
  menu: `${PREFIX}-menu`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.menu}`]: {
    paddingLeft: theme.spacing(1),
  }
}));

export default function ActionsMenu(props) {

  const {
    readOnly, 
    role, 
    request, 
    isLeadAnalyst, 
    isSupportAnalyst, 
    handleAssignMe, 
    handleUnassign,
    handleDelete, 
    handleRefreshForm, 
    setBackdropOpen,
    snackbarState,
    handleCloseSnackbar
  } = props;
  const {userInfo} = React.useContext(UserInfoContext);
  const {t} = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState({
    dialogAssign: false,
    dialogAssigneeDetails: false,
    dialogRequesterDetails: false,
    dialogNoLeadAssignSupport: false,
    dialogAssignAsSupport: false,
    dialogNoLeadUnassign: false,
    dialogDelete: false,
    summaryDrawer: false,
    manageTeamDrawer: false,
  });

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleBlur=(event)=>{
    if (event.key === 'Tab') {
      event.preventDefault();
      setAnchorEl(null);
    }
  }

  const toggleDialog = (state, value) => {
    setOpen({...open, [state]: value});
  };

  const assigneeDetailsMenuItem = () => {
    if((role===UserRole.Researcher && 
    request.primaryAnalyst.account.length >0) ||
    (role===UserRole.Analyst &&
    (request.status.id === RequestStatusId.Approved ||
    request.status.id === RequestStatusId.Denied))){
      return (
        <MenuItem
          key="assigneeDetailsMenuItem"
          id="toolbar-assigneeDetails"
          onClick={(e) => {
            handleClose(e);
            toggleDialog('dialogAssigneeDetails', true);
          }}
        >
          <ListItemText
            primary={
              <Typography variant="body2">{t('requestForm.toolbar.assigneeDetails')}</Typography>
            }
          />
        </MenuItem>
      );
    }
  };

  const requesterDetailsMenuItem = () => {
    return (
      <MenuItem
        key="requesterDetailsMenuItem"
        id="toolbar-requesterDetails"
        onClick={(e) => {
          handleClose(e);
          toggleDialog('dialogRequesterDetails', true);
        }}
      >
        <ListItemText
          primary={
            <Typography variant="body2">{t('requestForm.toolbar.requesterDetails')}</Typography>
          }
        />
      </MenuItem>
    );
  };

  const deleteMenuItem = () => {
    if(!readOnly &&
    request.status.id === RequestStatusId.Draft &&
    !(request.hasBeenReviewed || request.hasBeenSubmitted)){
      return (
        <MenuItem
          key="deleteMenuItem"
          id="toolbar-delete"
          onClick={(e) => {
            handleClose(e);
            handleCloseSnackbar();
            toggleDialog('dialogDelete', true);
          }}
        >
          <ListItemText
            primary={<Typography variant="body2">{t('requestForm.toolbar.delete')}</Typography>}
          />
        </MenuItem>
      );
    }
  };

  const assignAsLeadMenuItem = () => {
    if(request.status.id !== RequestStatusId.Approved &&
    request.status.id !== RequestStatusId.Denied){
      return (
        <MenuItem
          key="assignAsLeadMenuItem"
          id="toolbar-assignAsLead"
          onClick={(e) => {
            handleClose(e);
            if(!isSupportAnalyst){
              toggleDialog('dialogAssign', true);
            }else{
              handleAssignMe(request.supportAnalysts.find(a=>a.account===userInfo.id).phoneNumber, 'lead');
            }
          }}
          open={open.dialogAssign}
        >
          <ListItemText
            primary={
              <Typography variant="body2">
                {t("requestForm.toolbar.assignMeLead")}
              </Typography>
            }
          />
        </MenuItem>
      );
    }
  };

  const assignAsSupportMenuItem = () => {
    if(request.status.id !== RequestStatusId.Approved &&
    request.status.id !== RequestStatusId.Denied){
      return (
        <MenuItem
          key="assignAsSupportMenuItem"
          id="toolbar-assignAsSupport"
          onClick={(e) => {
            handleClose(e);
            handleCloseSnackbar();
            if(!isLeadAnalyst){
              toggleDialog('dialogAssignAsSupport', true);
            }else{
              toggleDialog('dialogNoLeadAssignSupport', true);
            }
          }}
        >
          <ListItemText
            primary={
              <Typography variant="body2">
                {t("requestForm.toolbar.assignMeSupport")}
              </Typography>
            }
          />
        </MenuItem>
      );
    }
  };

  const unassignMenuItem = () => {
    if(request.status.id !== RequestStatusId.Approved &&
    request.status.id !== RequestStatusId.Denied){
      return (
        <MenuItem
          key="unassignMenuItem"
          id="toolbar-unassign"
          onClick={(e) => {
            if(isLeadAnalyst) {
              handleClose(e);
              toggleDialog('dialogNoLeadUnassign', true);
            }else if(isSupportAnalyst){
              handleClose(e);
              handleUnassign()
            }
          }}
        >
          <ListItemText
            primary={
              <Typography variant="body2">{t("requestForm.toolbar.unassignMe")}</Typography>
            }
          />
        </MenuItem>
      );
    }
  };

  const manageAssigneesMenuItem = () => {
    if(request.status.id !== RequestStatusId.Approved &&
    request.status.id !== RequestStatusId.Denied){
      return (
        <MenuItem 
          key="manageAssigneesMenuItem"
          id="toolbar-manageAssignees"
          onClick={(e)=>{
            handleClose(e);
            toggleDialog("manageTeamDrawer", true)
        }}>
          <ListItemText
            primary={
              <Typography variant="body2">{t('requestForm.toolbar.manageAssignees')}</Typography>
            }
          />
        </MenuItem>
      );
    }
  };

  const summaryMenuItem = () => {
    return (
      <MenuItem 
        key="summaryMenuItem"
        id="toolbar-summary"
        onClick={(e)=>{
          handleClose(e);
          toggleDialog("summaryDrawer", true)
        }}
      >
        <ListItemText
          primary={<Typography variant="body2">{t('requestForm.toolbar.summary')}</Typography>}
        />
      </MenuItem>
    );
  };

  let menuItems=[];
  if(role===UserRole.Researcher){
    menuItems=[
      assigneeDetailsMenuItem(),
      requesterDetailsMenuItem(),
      summaryMenuItem(),
      deleteMenuItem()
    ]
  }else if(role===UserRole.Analyst){
    if(isLeadAnalyst){
      menuItems=[
        assignAsSupportMenuItem(),
        unassignMenuItem(),
        manageAssigneesMenuItem(),
        assigneeDetailsMenuItem(),
        requesterDetailsMenuItem(),
        summaryMenuItem()
      ]
    }else if(isSupportAnalyst){
      menuItems=[
        assignAsLeadMenuItem(),
        unassignMenuItem(),
        manageAssigneesMenuItem(),
        assigneeDetailsMenuItem(),
        requesterDetailsMenuItem(),
        summaryMenuItem()
      ]
    }else{
      menuItems=[
        assignAsLeadMenuItem(),
        assignAsSupportMenuItem(),
        manageAssigneesMenuItem(),
        assigneeDetailsMenuItem(),
        requesterDetailsMenuItem(),
        summaryMenuItem()
      ]
    }
  }

  return (
    <Root className={classes.menu}>
      <IconButton
        id="actions-menu-btn"
        onClick={handleClick}
        aria-controls="actions-menu"
        aria-haspopup="true"
        aria-label="Actions menu"
        edge="end"
        onKeyPress={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.key === 'Enter') {
            handleClick(e);
          }
        }}
        size="large">
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        handleClose={handleClose}
        handleBlur={handleBlur}
        content={menuItems}
      />

      <DialogAssign
        open={open.dialogAssign}
        toggleDialog={()=>toggleDialog('dialogAssign', false)}
        handleAssignMeAs={handleAssignMe}
        isAssignLead={true}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarAssignPrimary}
            handleClose={()=>handleCloseSnackbar("snackbarAssignPrimary")}
          />
        }
      />
      <DialogNoLead
        open={open.dialogNoLeadUnassign}
        toggleDialog={() => toggleDialog("dialogNoLeadUnassign", false)}
        handleSubmit={() => {
          toggleDialog("dialogNoLeadUnassign", false);
          handleUnassign();
        }}
        text={{
          title: t('dialog.noLeadNoLeadAssigned'),
          titleClose: t('dialog.noLeadNoLeadClose'), 
          main: t('dialog.noLeadMainText'), 
          submit: t('dialog.noLeadContinue')
        }}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarUnassign}
            handleClose={()=>handleCloseSnackbar("snackbarUnassign")}
          />
        }
      />
      <DialogAssign
        open={open.dialogAssignAsSupport}
        toggleDialog={()=>toggleDialog('dialogAssignAsSupport', false)}
        handleAssignMeAs={handleAssignMe}
        isAssignLead={false}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarAssignSupport}
            handleClose={()=>handleCloseSnackbar("snackbarAssignSupport")}
          />
        }
      />
      <DialogNoLead
        open={open.dialogNoLeadAssignSupport}
        toggleDialog={()=>toggleDialog('dialogNoLeadAssignSupport', false)}
        handleSubmit={() => {
          toggleDialog("dialogNoLeadAssignSupport", false);
          handleAssignMe(null, 'support');
        }}
        text={{
          title: t('dialog.noLeadNoLeadAssigned'),
          titleClose: t('dialog.noLeadNoLeadClose'), 
          main: t('dialog.noLeadMainText'), 
          submit: t('dialog.noLeadContinue')
        }}
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarAssignSupportNoLead}
            handleClose={()=>handleCloseSnackbar("snackbarAssignSupportNoLead")}
          />
        }
      />

      <ManageTeamDrawer
        open={open.manageTeamDrawer}
        clickHandler={()=>toggleDialog('manageTeamDrawer', false)}
        data={{
          'statusId':request.status.id,
          'requestId':request.id,
          'leadAnalyst':request.primaryAnalyst,
          'supportAnalysts':request.supportAnalysts
        }}
        refreshRequest={handleRefreshForm}
        setBackdropOpen={setBackdropOpen}
      />
      <DialogAssigneeDetails
        toggleDialog={()=>toggleDialog('dialogAssigneeDetails', false)}
        open={open.dialogAssigneeDetails}
        account={request.primaryAnalyst}
        supportAnalysts={request.supportAnalysts}
        statusHead={request.status.id}
      />
      <DialogRequesterDetails
        toggleDialog={()=>toggleDialog('dialogRequesterDetails', false)}
        open={open.dialogRequesterDetails}
        account={request.researcher}
      />
      <SummaryDrawer
        open={open.summaryDrawer}
        clickHandler={()=>toggleDialog("summaryDrawer", false)}
        data={request}
      />
      <DialogDelete 
        toggleDialog={()=>toggleDialog('dialogDelete', false)}
        handleDelete={handleDelete} 
        open={open.dialogDelete} 
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarDelete}
            handleClose={()=>handleCloseSnackbar("snackbarDelete")}
          />
        }
      />
    </Root>
  );
}
ActionsMenu.propTypes={
  readOnly: PropTypes.bool.isRequired,
  role: PropTypes.number.isRequired,
  request: PropTypes.object.isRequired,
  isLeadAnalyst: PropTypes.bool.isRequired,
  isSupportAnalyst: PropTypes.bool.isRequired,
  handleAssignMe: PropTypes.func.isRequired,
  handleUnassign: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleRefreshForm: PropTypes.func.isRequired,
  setBackdropOpen: PropTypes.func.isRequired,
  snackbarState: PropTypes.object.isRequired,
  handleCloseSnackbar: PropTypes.func.isRequired,
}